import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import { stagger, prefersReducedMotion } from '../../utils/motion'

/**
 * JPG Academy Stats Row
 *
 * A horizontal row of key-value stat items, used across dashboards,
 * summaries, and data pages. Replaces 7+ bespoke stat bar implementations.
 *
 * Usage:
 *   <StatsRow stats={[{ label: 'Total', value: 42 }, { label: 'Pending', value: 3, warn: true }]} />
 *   <StatsRow stats={stats} variant="compact" divider />
 */

export interface StatItem {
	label: string
	value: React.ReactNode
	/** Optional sub-label below the value */
	sub?: string
	/** Highlight the stat in warning/danger color */
	warn?: boolean
	/** Optional color override for the value */
	valueColor?: string
}

export interface StatsRowProps {
	stats: StatItem[]
	/** Visual variant */
	variant?: 'default' | 'compact' | 'bordered'
	/** Show vertical dividers between items */
	divider?: boolean
	/** Additional classes on the outer container */
	className?: string
}

const containerVariants = {
	default: 'gap-5 p-4 px-5 bg-card border border-border rounded-lg shadow-sm',
	compact: 'gap-4 p-3 px-4 bg-card border border-border rounded-lg shadow-sm',
	bordered: 'gap-5 p-4 px-5 bg-card border border-border rounded-lg shadow-sm ring-1 ring-navy/5',
}

export function StatsRow({
	stats,
	variant = 'default',
	divider = false,
	className,
}: StatsRowProps) {
	const reduced = prefersReducedMotion()

	return (
		<motion.div
			className={cn(
				'flex items-center flex-wrap',
				containerVariants[variant],
				className,
			)}
			initial={reduced ? undefined : 'hidden'}
			animate="visible"
			variants={stagger.container}
		>
			{stats.map((stat, i) => (
				<React.Fragment key={stat.label}>
					{divider && i > 0 && (
						<div className="w-px h-5 bg-border flex-shrink-0" aria-hidden />
					)}
					<motion.div
						className={cn(
							'flex',
							variant === 'compact'
								? 'items-baseline gap-1'
								: 'flex-col gap-1',
						)}
						variants={stagger.item}
					>
						{variant === 'compact' ? (
							<>
								<span
									className={cn(
										'text-lg font-bold leading-none',
										stat.warn ? 'text-error' : stat.valueColor || 'text-foreground',
									)}
								>
									{stat.value}
								</span>
								<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									{stat.label}
								</span>
							</>
						) : (
							<>
								<span
									className={cn(
										'text-[1.75rem] font-bold leading-none tracking-tight',
										stat.warn ? 'text-error' : stat.valueColor || 'text-foreground',
									)}
								>
									{stat.value}
								</span>
								<span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
									{stat.label}
								</span>
								{stat.sub && (
									<span className="text-[0.6875rem] text-muted-foreground">
										{stat.sub}
									</span>
								)}
							</>
						)}
					</motion.div>
				</React.Fragment>
			))}
		</motion.div>
	)
}
